import {
    Button,
    dataset,
    datasetsEnums,
    chartAxisFormatEnums as defaultFormatters,
    DynamicChart,
    formatFunction,
    getDefaultChartOptions,
    Grid,
    MuiIcons,
    Skeleton,
    SmartSelect,
    Typography
} from "@leap4/frontend";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "chart.js";
import { useHistory } from "react-router-dom";

import moment from "moment";
import { UserViewsContext } from "../../../contexts/userViewsContext";
import { urlService } from "../../routing/urlsService";
import { EntitiesProperties } from "../../../utils/enums";
import {
    getDatasetsForGraph,
    getDatasetsForPartnerSeries,
    getLabelsForGraph,
    getXAxisFormatFunc,
    graphDataProgressions,
    indexToTimeInterval,
    TimeDisplay
} from "./charts/charts";

const { OpenInNew: OpenInNewIcon } = MuiIcons;
const {
    COST,
    IMPRESSIONS,
    PROFIT,
    REVENUE,
    REQUESTS,
    NET_COST,
    NET_REVENUE,
    NET_PROFIT,
    COMPLETION,
    VCR,
    FILL_RATE,
    CPM,
    RPM,
    INCOMING_REQUESTS,
    AD_FILL_RATE,
    AD_REQUESTS,
    SERVICE_COST
} = EntitiesProperties.NUMERIC.ALL
const allOptionalCompares = [
    { name: 'Net Profit', value: NET_PROFIT },
    { name: 'Profit', value: PROFIT },
    { name: 'Net Revenue', value: NET_REVENUE },
    { name: 'Revenue', value: REVENUE },
    { name: 'Net Cost', value: NET_COST },
    { name: 'Cost', value: COST },
    { name: 'Impressions', value: IMPRESSIONS },
    { name: 'Ad Requests', value: REQUESTS },
    { name: 'Completion', value: COMPLETION },
    { name: 'VCR', value: VCR },
    { name: 'Ad Fill Rate', value: AD_FILL_RATE },
    { name: 'Fill Rate', value: FILL_RATE },
    { name: 'CPM', value: CPM },
    { name: 'RPM', value: RPM },
    { name: 'Incoming Requests', value: INCOMING_REQUESTS },
    { name: 'Serving Cost', value: SERVICE_COST }
]

export type PartnerSide = 'all' | 'supply' | 'demand'

const partnerSideOptions: { name: string; value: PartnerSide }[] = [
    { name: 'All', value: 'all' },
    { name: 'Supply', value: 'supply' },
    { name: 'Demand', value: 'demand' },
]


export interface IOverviewGraphResponse {
    selectedDates: IOverviewGraphDataNew
    dayAgo: IOverviewGraphDataNew,
    weekAgo: IOverviewGraphDataNew
    monthAgo: IOverviewGraphDataNew,
    specificComparisonDate?: IOverviewGraphDataNew
    partnerSeries?: IOverviewPartnerGraphSeries[]
    hasMorePartnerSeries?: boolean
}

export interface IOverviewPartnerGraphSeries {
    id: string
    labelName: string
    isOthers?: boolean
    data: Array<graphDataTillMidnightNew>
}

export interface IComparisonGraphProps {
    isLoading: boolean;
    graphData: IOverviewGraphResponse
    startDate: string;
    endDate: string;
    setNewInterval: (interval: number) => void
    graphIntervalIndx: number
    tension?: number,
    onPartnersFilterChange?: (params: { side: PartnerSide; rankingMetric: string }) => void
    onLoadMorePartners?: () => void
}

type axisFormatType = formatFunction | defaultFormatters

export interface graphDataTillMidnightNew {
    id?: string
    time: Date
    totals: {
        revenue: number;
        cost: number;
        impressions: number;
        requests: number;
    }
}


export interface IOverviewGraphDataNew {
    data: Array<graphDataTillMidnightNew>;
}

export default function ComparisonOverviewGraph(props: IComparisonGraphProps) {
    const {
        isLoading,
        startDate,
        endDate,
        graphData,
        setNewInterval,
        graphIntervalIndx,
        tension = 0.5,
        onPartnersFilterChange,
        onLoadMorePartners,
    } = props
    const { userViews } = useContext(UserViewsContext)
    const [optionalCompares, setOptionalCompares] = useState([])
    const [labelsForGraph, setLabelsForGraph] = useState<string[]>(null)
    const [selectedGraphDataProgressionIdx, setGraphDataProgressionIdx] = useState(0);
    const [graphDataType, setGraphDataType] = useState<{ name: string, value: datasetsEnums }[]>(graphDataProgressions)
    const [selectedCompareIdx, setSelectedCompareIdx] = useState(0);
    const [selectedSideIdx, setSelectedSideIdx] = useState(0);
    const [datasets, setDatasets] = useState([])
    const [xAxisFormat, setXAxisFormat] = useState<axisFormatType>(null)
    const [yAxisFormat, setYAxisFormat] = useState<axisFormatType>(defaultFormatters.USD)
    const lastEmittedKey = useRef<string>('all|')
    const history = useHistory()
    const partnerSeriesRef = useRef<IOverviewPartnerGraphSeries[]>([])

    useEffect(() => {
        partnerSeriesRef.current = graphData?.partnerSeries ?? []
    }, [graphData])

    const selectedSideForOptions = partnerSideOptions[selectedSideIdx].value
    const chartOptions = useMemo(() => {
        if (selectedSideForOptions === 'all') return undefined
        const base: any = getDefaultChartOptions('Comparison Graph', xAxisFormat as any, tension)
        return {
            ...base,
            plugins: {
                ...(base.plugins ?? {}),
                legend: {
                    ...((base.plugins && base.plugins.legend) || {}),
                    onClick(evt: any, legendItem: any, legend: any) {
                        const native = evt?.native as MouseEvent | undefined
                        const series = partnerSeriesRef.current?.[legendItem.datasetIndex]
                        const shouldNavigate =
                            !!(native && (native.ctrlKey || native.metaKey)) &&
                            series && !series.isOthers && !!series.id
                        if (shouldNavigate) {
                            native?.preventDefault?.()
                            history.push(urlService.partnerOverview(series.id, true))
                            return
                        }
                        ;(Chart as any).defaults.plugins.legend.onClick.call(this, evt, legendItem, legend)
                    },
                },
            },
        }
    }, [selectedSideForOptions, xAxisFormat, tension])

    const generateChartProps = () => {
        if (!graphData) return;
        const timeInterval = indexToTimeInterval(graphIntervalIndx)
        const labels = getLabelsForGraph(startDate, endDate, timeInterval)

        setLabelsForGraph(labels)

        const xAxisFormat = getXAxisFormatFunc(startDate, endDate, timeInterval);
        setXAxisFormat(prevState => xAxisFormat)
        generateDatasetsAndAxisFormat()
    }

    useEffect(generateChartProps, [graphData, selectedCompareIdx, selectedGraphDataProgressionIdx, graphIntervalIndx, selectedSideIdx]);

    useEffect(() => {
        const { graphViews } = userViews.activePreset
        setOptionalCompares(getOptionalComparesByViews(graphViews))
    }, [userViews])

    // Notify parent when the partners filter or its ranking metric meaningfully changes.
    // Deduped via ref so: initial mount with side='all' is silent, and metric changes
    // while side='all' don't trigger refetches (the chart re-renders locally instead).
    useEffect(() => {
        if (!onPartnersFilterChange) return
        const side = partnerSideOptions[selectedSideIdx].value
        const rankingMetric = optionalCompares[selectedCompareIdx]?.value || 'revenue'
        const key = side === 'all' ? 'all|' : `${side}|${rankingMetric}`
        if (key === lastEmittedKey.current) return
        lastEmittedKey.current = key
        onPartnersFilterChange({ side, rankingMetric })
    }, [selectedSideIdx, selectedCompareIdx, optionalCompares])

    const getOptionalComparesByViews = (graphViews) => {
        return graphViews?.map(view => {
            return allOptionalCompares.find(comp => {
                return comp.value === view
            })
        }) || []
    }
    const generateDatasetsAndAxisFormat = () => {
        if (!graphData) {
            return;
        }
        const type = graphDataProgressions[selectedGraphDataProgressionIdx].value;
        const intervalType = indexToTimeInterval(graphIntervalIndx);
        let currDatasets: dataset[] = []
        const selectedEntity = optionalCompares[selectedCompareIdx]?.value || REVENUE
        const getDatasets = (datasetType) => {
            const selectedSide = partnerSideOptions[selectedSideIdx].value
            if (selectedSide !== 'all') {
                return getDatasetsForPartnerSeries(graphData.partnerSeries || [], selectedEntity, intervalType, datasetType)
            }

            return getDatasetsForGraph(graphData, selectedEntity, intervalType, datasetType, graphIntervalIndx)
        }

        switch (selectedEntity) {
            case COST:
            case PROFIT:
            case REVENUE:
            case NET_REVENUE:
            case NET_COST:
            case NET_PROFIT:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasets(type)
                setYAxisFormat(defaultFormatters.USD)
                break;
            case IMPRESSIONS:
            case COMPLETION:
            case REQUESTS:
            case AD_REQUESTS:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasets(type);
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
            case VCR:
            case FILL_RATE:
            case AD_FILL_RATE:
                setGraphDataType([{ name: 'Absolute', value: datasetsEnums.Actual }])
                currDatasets = getDatasets('Absolute');
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
            case CPM:
            case RPM:
                setGraphDataType([{ name: 'Absolute', value: datasetsEnums.Actual }])
                currDatasets = getDatasets('Absolute');
                setYAxisFormat(defaultFormatters.USD)
                break;
            default:
                setGraphDataType(graphDataProgressions)
                currDatasets = getDatasets(type);
                setYAxisFormat(defaultFormatters.NUMBER)
                break;
        }
        setDatasets(currDatasets)
    }

    const getBucketsForLabels = () => {
        return moment(startDate).isSame(endDate, 'day') && moment(startDate).isSame(moment(), 'day') ? graphData.dayAgo.data : graphData.selectedDates.data
    }


    if (isLoading || !optionalCompares) {
        return <Skeleton sx={{ borderRadius: "16px" }} variant={"rounded"} height={"100%"} animation={"wave"} />
    }

    if (!graphData) {
        return <Typography variant={"overline"}>No Data in the selected date range</Typography>
    }
    const selectedSide = partnerSideOptions[selectedSideIdx].value
    if (selectedSide !== 'all' && graphData.partnerSeries && graphData.partnerSeries.length === 0) {
        return <Typography variant={"overline"}>No Data in the selected date range</Typography>
    }
    if (!labelsForGraph || (selectedSide === 'all' && datasets.length == 0)) {
        generateChartProps();
    }
    const shouldShowLoadMorePartners = selectedSide !== 'all' && graphData.hasMorePartnerSeries && Boolean(onLoadMorePartners)
    const navigablePartners = selectedSide !== 'all'
        ? (graphData.partnerSeries ?? []).filter(s => !s.isOthers && s.id)
        : []
    return (
        <>
            <DynamicChart
                style={{ borderRadius: 16, boxShadow: 'none' }}
                tension={tension}
                chartOptions={chartOptions as any}
                datasets={datasets} labels={labelsForGraph} axisFormat={{ x: xAxisFormat, y: yAxisFormat }}
                title={"Comparison Graph"}>
                <Grid container gap={'15px'} sx={{ alignItems: 'flex-end', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Grid item>
                        <SmartSelect
                            selectProps={{ size: "small" }}
                            items={optionalCompares}
                            selectedItemInd={selectedCompareIdx}
                            setSelectedState={(val) => setSelectedCompareIdx(val)}
                            title={"Comparsion"} />
                    </Grid>

                    <Grid item>
                        <SmartSelect
                            selectProps={{ size: "small" }}
                            items={graphDataType}
                            selectedItemInd={selectedGraphDataProgressionIdx}
                            setSelectedState={(val) => setGraphDataProgressionIdx(val)}
                            title={"Data Type"} />
                    </Grid>

                    <Grid item>
                        <Grid container gap={'8px'} alignItems={"flex-end"} wrap={"nowrap"}>
                            <Grid item>
                                <SmartSelect
                                    selectProps={{ size: "small" }}
                                    items={partnerSideOptions}
                                    selectedItemInd={selectedSideIdx}
                                    setSelectedState={(val) => setSelectedSideIdx(val)}
                                    title={"Partners"} />
                            </Grid>

                            {shouldShowLoadMorePartners && (
                                <Grid item>
                                    <Button
                                        size={"small"}
                                        variant={"outlined"}
                                        disabled={isLoading}
                                        onClick={onLoadMorePartners}
                                        sx={{
                                            height: 38,
                                            minWidth: "auto",
                                            px: 1,
                                            py: 0,
                                            fontSize: "0.72rem",
                                            lineHeight: 1,
                                            whiteSpace: "nowrap"
                                        }}>
                                        Load More
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 'auto' }} sx={{ ml: { xs: 0, md: 'auto' }, mr: { xs: 0, md: '35px' } }}>
                        <SmartSelect
                            selectProps={{ size: "small" }}
                            items={TimeDisplay}
                            selectedItemInd={graphIntervalIndx}
                            setSelectedState={setNewInterval}
                            title={"Time Interval"} />
                    </Grid>
                </Grid>

                {navigablePartners.length > 0 && (
                    <Grid container gap={'6px'} sx={{ flexWrap: 'wrap', mt: 1 }}>
                        {navigablePartners.map(s => (
                            <Grid item key={s.id}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<OpenInNewIcon fontSize="small" />}
                                    onClick={() => history.push(urlService.partnerOverview(s.id, true))}
                                    sx={{ textTransform: 'none', py: 0, minWidth: 'auto' }}>
                                    {s.labelName}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DynamicChart>
        </>

    )
}
